import { is } from '../../base/BaseServices';
import { isAny } from '../modelElements/services/ModelingService';

import {
  getMid,
  asTRBL,
  getOrientation
} from 'diagram-js/lib/layout/LayoutUtil';

import {
  findFreePosition,
  generateGetNextPosition,
  getConnectedDistance
} from 'diagram-js/lib/features/auto-place/AutoPlaceUtil';


/**
 * Find the new position for the target element to
 * connect to source.
 *
 * @param  {djs.model.Shape} source
 * @param  {djs.model.Shape} element
 *
 * @return {Point}
 */
export function getNewShapePosition(source, element) {

  if (isTextAnnotation(element)) {
    return getTextAnnotationPosition(source, element);
  }

  if (isDataElement(element)) {
    return getDataElementPosition(source, element);
  }

  if (isFlowNode(element)) {
    return getFlowNodePosition(source, element);
  }
}

function isTextAnnotation(element) {
  return is(element, 'bpmn:TextAnnotation');
}

function isDataElement(element) {
  return isAny(element, ['bpmn:DataObjectReference', 'bpmn:DataStoreReference']);
}

function isFlowNode(element) {
  return is(element, 'bpmn:FlowNode');
}


/**
 * Always try to place element right of source;
 * compute actual distance from previous nodes in flow.
 */
export function getFlowNodePosition(source, element) {

  const sourceTrbl = asTRBL(source);
  const sourceMid = getMid(source);

  const horizontalDistance = getConnectedDistance(source, {
    filter: connection => is(connection, 'bpmn:SequenceFlow')
  });

  let margin = 30;
  const minDistance = 80;
  let orientation = 'left';

  if (isBoundaryEvent(source)) {
    orientation = getOrientation(source, source.host, -25);

    if (orientation.includes('top')) {
      margin *= -1;
    }
  }

  const position = {
    x: sourceTrbl.right + horizontalDistance + element.width / 2,
    y: sourceMid.y + getVerticalDistance(orientation, minDistance)
  };

  const nextPositionDirection = {
    y: {
      margin,
      minDistance
    }
  };

  return findFreePosition(source, element, position, generateGetNextPosition(nextPositionDirection));
}

function isBoundaryEvent(source) {
  return is(source, 'bpmn:BoundaryEvent');
}


function getVerticalDistance(orientation, minDistance) {
  if (orientation.includes('top')) {
    return -1 * minDistance;
  } else if (orientation.includes('bottom')) {
    return minDistance;
  } else {
    return 0;
  }
}


/**
 * Always try to place text annotations top right of source.
 */
export function getTextAnnotationPosition(source, element) {
  const sourceTrbl = asTRBL(source);

  const position = {
    x: sourceTrbl.right + element.width / 2,
    y: sourceTrbl.top - 50 - element.height / 2,
  };

  const nextPositionDirection = {
    y: {
      margin: -30,
      minDistance: 20,
    },
  };

  return findFreePosition(source, element, position, generateGetNextPosition(nextPositionDirection));
}


/**
 * Always put element bottom right of source.
 */
export function getDataElementPosition(source, element) {
  const sourceTrbl = asTRBL(source);

  const position = {
    x: sourceTrbl.right - 10 + element.width / 2,
    y: sourceTrbl.bottom + 40 + element.width / 2,
  };

  const nextPositionDirection = {
    x: {
      margin: 30,
      minDistance: 30,
    },
  };

  return findFreePosition(source, element, position, generateGetNextPosition(nextPositionDirection));
}